/**
 * The remediation ladder, and the half of it Phase 1 reaches.
 *
 *   L0  observe     inspect, diagnose, compare, validate, trace
 *   L1  safe        retry a job, replay a delivery, restart a consumer
 *   L2  state       rebuild a projection, repair a settlement
 *   L3  dangerous   canonical events, money, winning bidder, deletion
 *
 * Note what is NOT on this interface: `execute`. That is the Phase 1 boundary,
 * expressed the way ADR-30 expressed its own ("being outside the derivable route
 * table is what makes it structurally impossible, not merely forbidden"). A
 * runbook here can be defined, registered, and PREVIEWED, and previewing is a
 * pure read of what it would change. Nothing in this package can run one,
 * because there is no method to call.
 *
 * That is not ceremony. An AI-shaped operations tool earns trust by being right
 * about diagnoses for a while before it is given hands, and the cheapest way to
 * guarantee it has no hands during that period is for the hands not to exist.
 *
 * When execution ships it is additive: `execute` arrives alongside the route
 * table that carries it and the approval record that gates it, reusing ADR-29's
 * preview-hash / single-use / TTL mechanism (`ai_approvals`) rather than
 * inventing a second one.
 */

import type { EvidenceBag, Scope } from './finding.js';

export const REMEDIATION_LEVELS = ['observe', 'safe', 'state', 'dangerous'] as const;
export type RemediationLevel = (typeof REMEDIATION_LEVELS)[number];

/** L0 is not a runbook; it is what the rest of this package already does. */
export type RunbookLevel = Exclude<RemediationLevel, 'observe'>;

export const RISK = ['low', 'medium', 'high'] as const;
export type Risk = (typeof RISK)[number];

/**
 * What a runbook would do, established by reading only.
 *
 * `willNotChange` is as load-bearing as `willChange`. The question an operator
 * has at 2am is not "what does this fix" but "what can this break", and a
 * repair that names the canonical event log as untouched is a repair somebody
 * will actually authorize.
 */
export interface RunbookPreview {
  readonly runbook: string;
  readonly level: RunbookLevel;
  readonly risk: Risk;
  readonly scope: Scope;
  /** Why this runbook is being proposed, in terms of the finding that proposed it. */
  readonly reason: string;
  /** Fully-qualified targets, e.g. 'sale_result_projection.last_projected_seq'. */
  readonly willChange: readonly string[];
  readonly willNotChange: readonly string[];
  /** The state the system would be in afterwards, as far as a read can tell. */
  readonly expected: EvidenceBag;
  /**
   * Preconditions that do NOT currently hold. A non-empty list means this
   * runbook must not run even once execution exists, and saying so during
   * preview is the point: the conditions are checked before anybody is asked to
   * approve, not after.
   */
  readonly blockedBy: readonly string[];
}

export interface RunbookContext<S> {
  readonly snapshot: S;
  readonly scope: Scope;
  readonly now: () => number;
}

/**
 * A registered runbook. Preview only, by construction - see the header.
 */
export interface Runbook<S> {
  /** SCREAMING_SNAKE, matching the `runbook` field on findings that propose it. */
  readonly id: string;
  readonly title: string;
  readonly level: RunbookLevel;
  readonly risk: Risk;
  /** The finding codes this runbook is a candidate response to. */
  readonly addresses: readonly string[];
  readonly preview: (ctx: RunbookContext<S>) => RunbookPreview;
}

export class RunbookRegistry<S> {
  private readonly byId = new Map<string, Runbook<S>>();

  constructor(runbooks: readonly Runbook<S>[] = []) {
    for (const r of runbooks) this.register(r);
  }

  register(runbook: Runbook<S>): void {
    if (this.byId.has(runbook.id)) throw new Error(`duplicate runbook id: ${runbook.id}`);
    this.byId.set(runbook.id, runbook);
  }

  get(id: string): Runbook<S> | undefined {
    return this.byId.get(id);
  }

  /** Candidates for a finding code, in registration order. */
  for(code: string): readonly Runbook<S>[] {
    return [...this.byId.values()].filter((r) => r.addresses.includes(code));
  }

  all(): readonly Runbook<S>[] {
    return [...this.byId.values()];
  }
}
